// Portions adapted from AnalyseTool (Apache-2.0), (c) Nikolai Davydov. Modified by apibim.
//
// Client-side cache for family meshes, stored in IndexedDB. It persists across sessions in both
// environments — the browser storefront and Revit's WebView2 profile — so re-opening a family's 3D
// preview is instant and never re-downloads the (potentially hundreds of KB) mesh.
//
// Keyed by the family slug, with the mesh's URL as the version tag: a republished version gets a new
// URL (its key embeds the version id), so the old entry is treated as a miss and overwritten. One
// entry per slug, no stale meshes lingering.

const DB_NAME = "dominus";
const VERSION = 4;
const MESH_STORE = "family-meshes";
const LOCAL_STORE = "local-packages";
const THUMB_STORE = "local-thumbnails";
const LOCAL_META_STORE = "local-meta";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(MESH_STORE))
        db.createObjectStore(MESH_STORE);
      if (!db.objectStoreNames.contains(LOCAL_STORE))
        db.createObjectStore(LOCAL_STORE);
      if (!db.objectStoreNames.contains(THUMB_STORE))
        db.createObjectStore(THUMB_STORE);
      if (!db.objectStoreNames.contains(LOCAL_META_STORE))
        db.createObjectStore(LOCAL_META_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

interface Entry<T> {
  v: string;
  d: T;
}

function get<T>(
  store: string,
  key: string,
  version: string,
): Promise<T | null> {
  return openDb()
    .then(
      (db) =>
        new Promise<T | null>((resolve) => {
          const req = db
            .transaction(store, "readonly")
            .objectStore(store)
            .get(key);
          req.onsuccess = () => {
            const entry = req.result as Entry<T> | undefined;
            resolve(entry && entry.v === version ? entry.d : null);
          };
          req.onerror = () => resolve(null);
        }),
    )
    .catch(() => null);
}

function put<T>(
  store: string,
  key: string,
  version: string,
  data: T,
): Promise<void> {
  return openDb()
    .then(
      (db) =>
        new Promise<void>((resolve) => {
          const tx = db.transaction(store, "readwrite");
          tx.objectStore(store).put({ v: version, d: data } as Entry<T>, key);
          tx.oncomplete = () => resolve();
          tx.onerror = () => resolve();
        }),
    )
    .catch(() => {});
}

/** One renderable part of a family mesh: all triangles sharing a material (approx colour + opacity). */
export interface FamilyMeshPart {
  color: number[]; // [r, g, b] 0-255
  opacity: number; // 0..1
  positions: number[];
  indices: number[];
}

export interface FamilyMeshData {
  available: boolean;
  reason?: string | null;
  parts?: FamilyMeshPart[];
}

export const getCachedMesh = (key: string, version: string) =>
  get<FamilyMeshData>(MESH_STORE, key, version);
export const setCachedMesh = (
  key: string,
  version: string,
  mesh: FamilyMeshData,
) => put(MESH_STORE, key, version, mesh);

// Local families get the whole extracted package cached, not just the mesh. Unlike a cloud family —
// where the metadata is already in the catalog listing and only the mesh has to be fetched — a local
// card's every field (category, year, units, parameters, types AND the mesh) comes from one
// expensive read of the .rfa on the Revit thread. Caching only the mesh would still leave the card
// waiting on that read to show anything.
//
// Keyed by the file's own path, versioned by its size and last-write time, so an edited family is a
// miss rather than a stale preview.

export const localPackageVersion = (sizeBytes: number, modifiedTicks: number) =>
  `${sizeBytes}:${modifiedTicks}:params-v3`;

export const getCachedLocalPackage = <T>(path: string, version: string) =>
  get<T>(LOCAL_STORE, path.toLowerCase(), version);
export const setCachedLocalPackage = <T>(
  path: string,
  version: string,
  pkg: T,
) => put(LOCAL_STORE, path.toLowerCase(), version, pkg);

// Grid thumbnails for local files, versioned the same way. Separate from the package store because
// they are wanted at a completely different time: every card on screen needs its thumbnail, while a
// package is only read for the one card somebody opened.

export const getCachedThumbnail = (path: string, version: string) =>
  get<string>(THUMB_STORE, path.toLowerCase(), version);
export const setCachedThumbnail = (
  path: string,
  version: string,
  pngBase64: string,
) => put(THUMB_STORE, path.toLowerCase(), version, pngBase64);

/** The two facts a grid card shows about a family it has already read: which Revit saved it, and in
 * which units. */
export interface LocalCardMeta {
  revitYear?: number | null;
  unitSystem?: string | null;
}

// Written alongside the package, and kept apart from it on purpose. The package carries the mesh,
// and a grid of forty cards reading forty packages just to pull two fields out would deserialise
// tens of megabytes to render two short strings.

export const getCachedLocalMeta = (path: string, version: string) =>
  get<LocalCardMeta>(LOCAL_META_STORE, path.toLowerCase(), version);
export const setCachedLocalMeta = (
  path: string,
  version: string,
  meta: LocalCardMeta,
) => put(LOCAL_META_STORE, path.toLowerCase(), version, meta);
